<template>
  <v-container fluid class="pa-6">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <!-- Page Title -->
    <v-row>
      <v-col>
        <h1 class="text-h4 font-weight-medium">Dashboard</h1>
        <p v-if="metrics" class="text-subtitle-1 grey--text">
          {{ dateRangeText }}
        </p>
      </v-col>
    </v-row>

    <!-- Header Filters -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="3">
        <v-select
          :items="dateRanges"
          label="Date Range"
          v-model="filters.dateRange"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details
        ></v-select>
      </v-col>
    </v-row>

    <!-- Metrics Summary Section -->
    <v-row>
      <v-col
        v-for="metric in summaryMetrics"
        :key="metric.title"
        cols="12"
        sm="6"
        md="4"
        lg="2"
      >
        <v-card class="rounded-xl" elevation="2">
          <v-card-text class="pa-4">
            <div class="d-flex justify-space-between align-start">
              <div>
                <p class="text-subtitle-2 grey--text text--darken-1 mb-1">
                  {{ metric.title }}
                </p>
                <p class="text-h4 font-weight-bold mb-1">{{ metric.value }}</p>
                <div
                  class="d-flex align-center"
                  v-if="filters.dateRange !== 'all_time'"
                >
                  <v-icon
                    :color="metric.change > 0 ? 'success' : 'error'"
                    size="18"
                  >
                    {{ metric.change > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                  </v-icon>
                  <span
                    :class="[
                      metric.change > 0 ? 'success--text' : 'error--text',
                      'text-body-2',
                      'font-weight-medium',
                    ]"
                  >
                    {{ Math.round(Math.abs(metric.change)) }}%
                  </span>

                  <span class="text-caption grey--text ml-2">
                    {{ metric.changePeriodText }}</span
                  >
                </div>
              </div>
              <v-avatar color="grey lighten-4" size="40" v-if="metric.icon">
                <v-icon color="black" icon="mdi-email" size="large"></v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row align="stretch">
      <!-- Revenue Trend -->
      <v-col cols="12" md="6">
        <v-card class="rounded-xl" elevation="2">
          <v-card-title>Revenue Trend</v-card-title>
          <v-card-text class="revenue-trend-card">
            <div v-for="item in revenueTrend" :key="item.month" class="mb-3">
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span class="font-weight-medium">{{ item.month }}</span>
                <span class="font-weight-bold"
                  >${{ item.amount.toLocaleString() }}</span
                >
              </div>
              <v-progress-linear
                :value="
                  (item.amount /
                    Math.max(...revenueTrend.map((i) => i.amount))) *
                  100
                "
                color="black"
                height="10"
                rounded
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- User Growth -->
      <v-col cols="12" md="6">
        <v-card class="rounded-xl" elevation="2">
          <v-card-title>User Growth</v-card-title>
          <v-card-subtitle>New users over the last 4 weeks</v-card-subtitle>
          <v-card-text>
            <div class="bar-chart-container">
              <div
                v-for="(growth, index) in userGrowth"
                :key="index"
                class="bar-item"
              >
                <div class="bar-value">
                  {{ growth.users.toLocaleString() }}
                </div>
                <div class="bar-wrapper">
                  <div
                    class="bar"
                    :style="{
                      height: (growth.users / maxUserGrowth) * 100 + '%',
                    }"
                  ></div>
                </div>
                <div class="bar-label">{{ growth.week }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top Performing Courses Section -->
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-xl" elevation="2">
          <v-card-title>Top Performing Courses</v-card-title>
          <v-card-subtitle
            >Most popular courses by enrollment and revenue</v-card-subtitle
          >
          <v-divider></v-divider>
          <v-list class="py-0">
            <template v-for="(course, index) in topCourses">
              <v-list-item :key="course.name">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{
                    course.name
                  }}</v-list-item-title>
                  <v-list-item-subtitle class="mt-2">
                    {{ course.enrollments }} enrollments &bull; ${{
                      course.revenue.toLocaleString()
                    }}
                    revenue
                  </v-list-item-subtitle>
                  <div class="d-flex align-center mt-2">
                    <v-progress-linear
                      :value="(course.completion / course.enrollments) * 100"
                      height="8"
                      rounded
                    ></v-progress-linear>
                    <span class="ml-3 text-caption font-weight-medium"
                      >{{
                        Math.round(
                          course.completion > course.enrollments
                            ? 100
                            : (course.completion / course.enrollments) * 100
                        )
                      }}%</span
                    >
                  </div>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    :color="
                      course.performance === 'High'
                        ? 'green lighten-4'
                        : 'orange lighten-4'
                    "
                    :text-color="
                      course.performance === 'High'
                        ? 'green darken-2'
                        : 'orange darken-4'
                    "
                    class="font-weight-bold"
                    small
                  >
                    {{ course.performance }} Performance
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-divider
                v-if="index < topCourses.length - 1"
                :key="`divider-${index}`"
              ></v-divider>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { format } from 'date-fns'

export default {
  name: 'DashboardPage',
  data() {
    return {
      filters: {
        dateRange: 'last_30_days',
      },
      dateRanges: [
        { text: 'Last 7 days', value: 'last_7_days' },
        { text: 'Last 30 days', value: 'last_30_days' },
        { text: 'Last 90 days', value: 'last_90_days' },
        { text: 'All Time', value: 'all_time' },
      ],
      metrics: null,
      loading: false,
    }
  },
  computed: {
    summaryMetrics() {
      if (!this.metrics) return []
      const startDateFormatted = format(
        new Date(this.metrics.start_date),
        'MMM d, yyyy'
      )
      const getChangePeriodText = (change) => {
        if (this.filters.dateRange === 'all_time') return ''
        return `since ${startDateFormatted}`
      }

      return [
        {
          title: 'Revenue',
          value: `$${this.metrics.revenue.value.toLocaleString()}`,
          change: this.metrics.revenue.change,
          icon: 'mdi-currency-usd',
          changePeriodText: getChangePeriodText(this.metrics.revenue.change),
        },
        {
          title: 'Active Users',
          value: this.metrics.active_users.value.toLocaleString(),
          change: this.metrics.active_users.change,
          icon: 'mdi-account-group-outline',
          changePeriodText: getChangePeriodText(
            this.metrics.active_users.change
          ),
        },
        {
          title: 'New Sign-ups',
          value: this.metrics.new_sign_ups.value.toLocaleString(),
          change: this.metrics.new_sign_ups.change,
          icon: 'mdi-account-plus-outline',
          changePeriodText: getChangePeriodText(
            this.metrics.new_sign_ups.change
          ),
        },
        {
          title: 'Avg Session Time',
          value: `${Math.round(this.metrics.average_session_time.value / 60)}m`,
          change: this.metrics.average_session_time.change,
          icon: 'mdi-clock-time-eight-outline',
          changePeriodText: getChangePeriodText(
            this.metrics.average_session_time.change
          ),
        },
        {
          title: 'Course Completions',
          value: this.metrics.course_completion.value.toLocaleString(),
          change: this.metrics.course_completion.change,
          icon: 'mdi-school-outline',
          changePeriodText: getChangePeriodText(
            this.metrics.course_completion.change
          ),
        },
        {
          title: 'Total Courses',
          value: this.metrics.total_courses.value.toLocaleString(),
          change: this.metrics.total_courses.change,
          icon: 'mdi-notebook-outline',
          changePeriodText: getChangePeriodText(
            this.metrics.total_courses.change
          ),
        },
      ]
    },
    revenueTrend() {
      if (!this.metrics) return []
      return Object.entries(this.metrics.revenue_trends).map(
        ([month, amount]) => ({
          month,
          amount,
        })
      )
    },
    userGrowth() {
      if (!this.metrics) return []
      return Object.entries(this.metrics.user_growth).map(([week, users]) => ({
        week,
        users,
      }))
    },
    maxUserGrowth() {
      if (!this.userGrowth || this.userGrowth.length === 0) return 0
      return Math.max(...this.userGrowth.map((g) => g.users))
    },
    topCourses() {
      if (!this.metrics) return []
      return this.metrics.top_performing_courses.map((course) => ({
        name: course.course_name,
        enrollments: course.enrollment,
        revenue: course.revenue,
        completion:
          course.completion > course.enrollment
            ? 100
            : (course.completion / course.enrollment) * 100,
        performance:
          course.completion / course.enrollment >= 0.5 ? 'High' : 'Low',
      }))
    },
    dateRangeText() {
      if (!this.metrics) return ''
      const start = format(new Date(this.metrics.start_date), 'MMM d, yyyy')
      const end = format(new Date(this.metrics.end_date), 'MMM d, yyyy')
      return `${start} - ${end}`
    },
  },
  watch: {
    'filters.dateRange': {
      handler(newVal) {
        this.fetchMetrics(newVal)
      },
    },
  },
  created() {
    this.fetchMetrics(this.filters.dateRange)
  },
  methods: {
    async fetchMetrics(dateRange) {
      this.loading = true
      try {
        const response = await this.$api.get('/admin/dashboard/metrics', {
          params: { date_range: dateRange },
        })
        this.metrics = response.data
      } catch (error) {
        // console.error('Error fetching dashboard metrics:', error)
        // Optionally, show a user-friendly error message
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* Using sans-serif fonts as requested, which is default in Vuetify with Roboto */
.font-weight-medium {
  font-weight: 500 !important;
}

.revenue-trend-card {
  min-height: 250px;
}

.bar-chart-container {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 250px;
}

.bar-item {
  text-align: center;
  width: 50px;
}

.bar-wrapper {
  height: 200px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.bar {
  background-color: black;
  width: 20px;
  border-radius: 4px 4px 0 0;
}

.bar-value {
  font-size: 1rem;
  font-weight: bold;
}

.bar-label {
  font-size: 0.875rem;
  color: grey;
}
</style>
